// IssueThread — the chronological rail of an issue's thread entries. Five
// entry types render as dated rows beside a vertical rail of nodes:
//   opened        — the auto-created first entry (red node)
//   comment       — free-text discussion (neutral node)
//   status_change — a workflow transition, shows from→to using
//                   TrackingStatusBadge pills inside a quiet shaded block.
//                   Node coloured only when transitioning TO resolved [emerald]
//                   or closed [slate-dark]; otherwise neutral.
//   action_raised — embeds a ThreadActionCard; neutral node
//   action_done   — records a completion as its own dated entry; neutral node
//
// Node colour discipline: only opened, →resolved, and →closed nodes carry
// colour — everything else is neutral so the thread reads calmly and the eye
// lands on the lifecycle-significant moments. Author ids resolve to display
// names via the shared batch lookup.

import { useMemo } from "react";
import { clsx } from "clsx";
import { useUserDisplays } from "../../../../../hooks/useUsers";
import { toBrisbaneDay } from "../../../../../utils/toBrisbaneDay";
import { renderMiniMarkdown } from "../../../../../utils/miniMarkdown";
import ThreadActionCard from "./ThreadActionCard";
import TrackingStatusBadge from "../../shared/TrackingStatusBadge";
import type {
  Issue,
  IssueThreadEntry,
} from "../../../../../types";

export interface IssueThreadProps {
  entries: IssueThreadEntry[];
  /** The denormalised action register, keyed by raising action_raised entry
   *  id. This is the CANONICAL source for an action's live state — the
   *  raising thread entry is immutable and never back-stamped with completion
   *  (`doneAt` only ever lives here + on the paired action_done entry), so the
   *  action card must read dueDate / doneAt / owner from this map, not from
   *  the action_raised thread-entry fields. */
  actions?: Issue["actions"];
  today?: Date;
  canWrite: boolean;
  /** Called with the raising action_raised entry id when its action is
   *  marked done from the embedded card. */
  onMarkActionDone: (entryId: string) => void;
}

const KIND_LABEL: Record<IssueThreadEntry["type"], string> = {
  opened: "Opened",
  comment: "Comment",
  status_change: "Status",
  action_raised: "Action raised",
  action_done: "Action marked done",
};

/** The node ring/fill class. Only lifecycle-significant moments carry colour. */
function nodeClass(entry: IssueThreadEntry): string {
  if (entry.type === "opened") {
    return "border-red-600 bg-red-600 text-white";
  }
  if (entry.type === "status_change") {
    if (entry.statusTo === "resolved") {
      return "border-emerald-600 bg-emerald-600 text-white";
    }
    if (entry.statusTo === "closed") {
      return "border-slate-700 bg-slate-700 text-white dark:border-slate-300 dark:bg-slate-300 dark:text-slate-900";
    }
  }
  return "border-slate-300 bg-white text-slate-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400";
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

export default function IssueThread({
  entries,
  actions,
  today,
  canWrite,
  onMarkActionDone,
}: IssueThreadProps) {
  // Resolve both the entry authors and any action owners so the action card
  // can show its internal owner's name alongside the party label. Owner ids
  // come from the canonical actions map (the raised entry's actionOwnerId
  // mirrors it, but the map is authoritative).
  const peopleIds = useMemo(() => {
    const ids = new Set<string>();
    for (const e of entries) ids.add(e.authorId);
    for (const a of Object.values(actions ?? {})) {
      if (a.ownerId) ids.add(a.ownerId);
    }
    return Array.from(ids);
  }, [entries, actions]);
  const { data: authors } = useUserDisplays(peopleIds);

  if (entries.length === 0) {
    return (
      <div
        data-testid="issue-thread-empty"
        className="py-6 text-center text-body-sm text-slate-500 dark:text-slate-400"
      >
        No thread entries yet.
      </div>
    );
  }

  return (
    <ol className="space-y-0">
      {entries.map((entry, index) => {
        const isLast = index === entries.length - 1;
        const authorName = authors.get(entry.authorId)?.name ?? entry.authorId;
        return (
          <li
            key={entry.entryId}
            data-testid={`thread-entry-${entry.entryId}`}
            className="flex gap-3"
          >
            <div className="flex flex-col items-center">
              <span
                data-testid={`thread-node-${entry.entryId}`}
                aria-hidden="true"
                className={clsx(
                  "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-[1.5px] text-[10px] font-bold",
                  nodeClass(entry),
                )}
              />
              {!isLast && (
                <span className="my-1 w-0.5 flex-1 bg-slate-200 dark:bg-slate-700" />
              )}
            </div>
            <div className="flex-1 pb-4">
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="text-body-sm font-semibold text-slate-900 dark:text-slate-100">
                  {authorName}
                </span>
                <span className="text-caption font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {KIND_LABEL[entry.type]}
                </span>
                <span className="ml-auto text-caption text-slate-500 dark:text-slate-400">
                  {formatWhen(entry.createdAt)}
                </span>
              </div>
              {entry.text &&
                (entry.type === "opened" ? (
                  // The opened entry carries the issue description, authored as
                  // markdown via the form toolbar — render the constrained
                  // grammar. Comments and other entries stay plain text.
                  <div className="mt-1 space-y-1 text-body-sm leading-relaxed text-slate-700 dark:text-slate-300">
                    {renderMiniMarkdown(entry.text)}
                  </div>
                ) : (
                  <p className="mt-1 text-body-sm leading-relaxed text-slate-700 dark:text-slate-300">
                    {entry.text}
                  </p>
                ))}
              {entry.type === "status_change" &&
                entry.statusFrom &&
                entry.statusTo && (
                  <div
                    data-testid={`status-change-block-${entry.entryId}`}
                    className="mt-1 inline-flex items-center gap-1.5 rounded-md border-l-2 border-slate-200 bg-slate-50 pl-3 pr-2 py-1 dark:border-slate-700 dark:bg-slate-800/60"
                  >
                    <TrackingStatusBadge kind="issue" status={entry.statusFrom} />
                    <span aria-hidden="true" className="text-caption text-slate-400 dark:text-slate-500">→</span>
                    <TrackingStatusBadge kind="issue" status={entry.statusTo} />
                  </div>
                )}
              {entry.type === "action_raised" &&
                (() => {
                  // Source the action's LIVE state from the canonical actions
                  // map (keyed by this raising entry's id) — never from the
                  // immutable action_raised entry, which carries no doneAt. The
                  // raised entry's descriptive fields are a fallback only for an
                  // action that somehow has no map record.
                  const record = actions?.[entry.entryId];
                  const description =
                    record?.description ?? entry.actionDescription;
                  if (!description) return null;
                  const ownerId = record?.ownerId ?? entry.actionOwnerId;
                  return (
                    <ThreadActionCard
                      action={{
                        description,
                        party: record?.party ?? entry.actionParty ?? "supplier",
                        ownerName: ownerId
                          ? (authors.get(ownerId)?.name ?? ownerId)
                          : undefined,
                        dueDate: record?.dueDate ?? entry.actionDueDate,
                        // doneAt is a full ISO instant from markActionDone;
                        // the card's lateness derivation expects a Brisbane
                        // calendar day, so normalise (dueDate is already one).
                        doneAt: record?.doneAt
                          ? toBrisbaneDay(record.doneAt)
                          : undefined,
                      }}
                      today={today}
                      canWrite={canWrite}
                      onMarkDone={() => onMarkActionDone(entry.entryId)}
                    />
                  );
                })()}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
